import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import cn from '../modules/cn'
import { isDevelopment } from '../modules'
import usePlaceholder from '../modules/usePlaceholder'
import { TYPE_CUSTOMER_PRODUCT, TYPE_MESSAGE_GROUP, TYPE_USER } from '../constants/avatar'
import Skeleton from './Skeleton'
import getAvatarUrl from '../modules/getAvatarUrl'

function Avatar({
  size,
  type,
  id,
  image, // DEPRECATED - use type and id instead
  alt,
  placeholder: overwritePlaceholder,
  count,
  className,
  cacheBust,
}) {
  const [imageLayer, setImageLayer] = useState(0)
  const placeholder = usePlaceholder(overwritePlaceholder, type, Number.isInteger(count))
  const avatarUrl = getAvatarUrl(type, id, size)

  const imageLayers = useMemo(() => {
    const output = []
    if (avatarUrl) {
      let url = avatarUrl
      if (cacheBust) {
        url += `?cache=${cacheBust}`
      }
      output.push(url)
    }

    if (image) {
      // eslint-disable-next-line no-console
      if (isDevelopment) {
        console.warn('Avatar - use of image is deprecated, please provide a type and id instead')
      }
      output.push(image)
    }

    output.push(placeholder)
    return output
  }, [avatarUrl, image, placeholder])

  useEffect(() => {
    setImageLayer(0)
  }, [imageLayers])

  if (!id && (count === null || count === undefined) && !image) {
    return <Skeleton icon className="h-10 w-10" />
  }

  if (imageLayer !== null && imageLayers[imageLayer]) {
    return (
      <img
        className={cn(
          'flex h-10 w-10 bg-gray-500 rounded-full flex-shrink-0 max-w-none object-cover',
          className,
        )}
        src={imageLayers[imageLayer]}
        alt={alt}
        onError={() => {
          const newIndex = imageLayer + 1
          if (!imageLayers[imageLayer] && Number.isInteger(count)) {
            setImageLayer(null)
            return
          }
          setImageLayer(newIndex)
        }}
      />
    )
  }

  return (
    <div
      className={cn(
        'font-display rounded-full justify-center items-center flex flex-shrink-0 w-10 h-10 uppercase text-white',
        'overflow-hidden bg-theme-500 font-extrabold text-lg',
        count > 99 && 'text-base',
        count > 999 && 'text-sm',
        count > 9999 && 'text-xs',
        className,
      )}
    >
      {count > 9999 ? '9999+' : count}
    </div>
  )
}

Avatar.propTypes = {
  size: PropTypes.number,
  type: PropTypes.oneOf([TYPE_USER, TYPE_MESSAGE_GROUP, TYPE_CUSTOMER_PRODUCT]),
  id: PropTypes.string,
  image: PropTypes.string,
  alt: PropTypes.string,
  placeholder: PropTypes.string,
  count: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  cacheBust: PropTypes.number,
}

Avatar.defaultProps = {
  size: 120,
  type: null,
  id: null,
  image: null,
  alt: null,
  placeholder: null,
  count: null,
  className: null,
  cacheBust: null,
}

export default Avatar
